import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, Input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AddEditFormGeneric } from '../../../../ui/generics';
import { FormBuilder } from '@angular/forms';
import { LabelsService, NotificationService } from '../../../../core/services';
import { IArticleLabel } from '../../core/interfaces/IArticleLabel';
import { ArticleLabelsService } from '../../core/services/ArticleLabelsService';
import { ArticleLabelsErrorResolverService } from '../../core/services/ArticleLabelsErrorResolverService';
import { ArticleLabelTypes } from '../../core/enums/ArticleLabelTypes';
import { IArticle } from '../../../../core/interfaces';
import { IRoundLabel } from '../../core/interfaces/IRoundLabel';
import { IFremdmarkeNutritionLabel } from '../../core/interfaces/IFremdmarkeNutritionLabel';
import { catchError, debounceTime, switchMap } from 'rxjs/operators';
import { forkJoin, merge, of, Subject } from 'rxjs';
import { UnicodeParser } from '../../../../core/parsers/UnicodeParser';
import { ILabelPreviewHtml } from '../../../../core/interfaces';
import { LABEL_PREVIEW_DEBOUNCE_MS } from '../../../etiketten/previews/label-preview.constants';
import { environment } from '../../../../../environments/environment';

@Component({
    selector: 'app-add-edit-round-label-form',
    templateUrl: './add-edit-round-label-form.component.html',
    styleUrl: './add-edit-round-label-form.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class AddEditRoundLabelFormComponent extends AddEditFormGeneric<IArticleLabel> {

  @Input() labelType: ArticleLabelTypes;
  @Input() article: IArticle;
  @Input() articleVariantIds: number[];

  public preview: ILabelPreviewHtml | null = null;
  public previewLoading = true;
  public readonly articleLabelTypes = ArticleLabelTypes;
  public readonly dateFormat = environment.defaultDateFormat;
  public readonly toolbarHiddenButtons: string[][] = [
    [
      'undo',
      'redo',
      'italic',
      'underline',
      'strikeThrough',
      'subscript',
      'superscript',
      'justifyLeft',
      'justifyCenter',
      'justifyRight',
      'justifyFull',
      'indent',
      'outdent',
      'insertUnorderedList',
      'insertOrderedList',
      'heading',
      'fontName'
    ],
    [
      'fontSize',
      'textColor',
      'backgroundColor',
      'customClasses',
      'link',
      'unlink',
      'insertImage',
      'insertVideo',
      'insertHorizontalRule',
      'toggleEditorMode'
    ]
  ];

  private readonly nutritionHeaderPlain = 'Nährwertangaben in g/100g:';
  private readonly nutritionHeaderGlyphs =
    `${UnicodeParser.replaceGlyphs('Nährwertangaben')} in g/100g:`;
  private readonly previewTick$ = new Subject<void>();

  public constructor(
    protected _fb: FormBuilder,
    protected _service: ArticleLabelsService,
    protected _notifyService: NotificationService,
    protected _errorResolver: ArticleLabelsErrorResolverService,
    private readonly _labelsService: LabelsService,
    private readonly _cdr: ChangeDetectorRef,
    private readonly _destroyRef: DestroyRef,
  ) {
    super(_fb, _service, _notifyService, _errorResolver);

    this.form = this._fb.group({
      name: [''],
      lineHeight: [null as number],
      ingredients: [''],
      content: [''],
      addBarCode: [false],
      showIngredients: [true],
      showNutrients: [true],
      allIngredientsAreBio: [false],
      originLine: [''],
      energyKj: [null as number],
      energyKcal: [null as number],
      fat: [null as number],
      saturatedFat: [null as number],
      carbohydrates: [null as number],
      sugar: [null as number],
      protein: [null as number],
      salt: [null as number],
      storageNote: [''],
      printMhd: [true],
      printNutrition: [true],
      charge: [''],
      mhd: [null as Date],
    });
  }

  public override ngOnInit(): void {
    super.ngOnInit();
    if (this.isFremdmarkeNutrition && !this.editObject) {
      this.form.patchValue({ name: this.article?.name ?? '' });
    }
    if ((this.isRound50x25 || this.isFremdmarkeNutrition) && !this.form.value.mhd) {
      this.form.patchValue({ mhd: this.shopwareMhd() });
    }
    merge(this.form.valueChanges, this.previewTick$).pipe(
      debounceTime(LABEL_PREVIEW_DEBOUNCE_MS),
      switchMap(() => this.requestPreviewHtml()),
      takeUntilDestroyed(this._destroyRef)
    ).subscribe(preview => {
      this.preview = preview;
      this.previewLoading = false;
      this._cdr.markForCheck();
    });
    this.previewTick$.next();
  }

  public get currentType(): ArticleLabelTypes {
    return (this.editObject?.type ?? this.labelType) as ArticleLabelTypes;
  }

  public get isFremdmarkeNutrition(): boolean {
    return this.currentType === ArticleLabelTypes.FremdmarkeNutrition;
  }

  public get isRound50x25(): boolean {
    return this.currentType === ArticleLabelTypes.Round50x25;
  }

  public saveEdit(): void {
    const object: IArticleLabel = {
      id: this.editObject.id,
      context: JSON.stringify(this.getContextPayload())
    };

    this._formService.update(object, this.editObject['id']).subscribe(() => {
      this._notifyService.pushSuccess('Erfolgreich', `Element wurde korrekt aktualisiert.`);
      this.formFinished.emit(true);
    }, err => {
      this.parseError(err);
      this.formFinished.emit(false);
    });
  }

  public saveNew(): void {
    if (!this.articleVariantIds?.length) {
      return;
    }

    forkJoin(this.articleVariantIds.map(id =>
      this._formService.create({
        articleId: this.article.id,
        articleVariantId: id,
        type: this.labelType,
        context: JSON.stringify(this.getContextPayload())
      })
    )).subscribe({
      next: () => {
        this._notifyService.pushSuccess('Erfolg', 'Etikett wurde erstellt.');
        this.formFinished.emit(true);
      },
      error: err => {
        this.parseError(err);
        this.formFinished.emit(false);
      }
    });
  }

  protected override patchForm(): void {
    if (this.isFremdmarkeNutrition) {
      const context = JSON.parse(this.editObject.context || '{}') as IFremdmarkeNutritionLabel;
      this.form.patchValue({
        ...context,
        name: context.name || this.article?.name || '',
        printMhd: context.printMhd ?? true,
        printNutrition: context.printNutrition ?? true,
        mhd: this.parseMhd(context.mhd) ?? this.shopwareMhd(),
      });
      return;
    }

    const context = JSON.parse(this.editObject.context || '{}') as IRoundLabel;

    if (this.currentType === ArticleLabelTypes.Round50x50 && context.content) {
      context.content = this.stripNutritionHeader(context.content);
    }

    this.form.patchValue({
      ...context,
      printMhd: context.printMhd ?? true,
      mhd: this.parseMhd(context.mhd) ?? this.shopwareMhd(),
    });
  }

  protected override afterLoadForm(): void {
    this.previewLoading = true;
    this.previewTick$.next();
    this._cdr.markForCheck();
  }

  private requestPreviewHtml() {
    const variantId = this.editObject?.articleVariantId ?? this.articleVariantIds?.[0];
    const type = this.currentType;
    if (!variantId || !type) {
      return of(null);
    }

    return this._labelsService.previewHtml({
      articleLabelType: type,
      articleVariantId: variantId,
      context: JSON.stringify(this.getContextPayload()),
    }).pipe(
      catchError(() => of(this.preview))
    );
  }

  private getContextPayload(): object {
    const value = this.form.getRawValue();
    if (this.isFremdmarkeNutrition) {
      return {
        name: (value.name || '').trim(),
        lineHeight: value.lineHeight,
        originLine: value.originLine || '',
        ingredients: this.prepareRichTextContent(value.ingredients) || '',
        energyKj: value.energyKj,
        energyKcal: value.energyKcal,
        fat: value.fat,
        saturatedFat: value.saturatedFat,
        carbohydrates: value.carbohydrates,
        sugar: value.sugar,
        protein: value.protein,
        salt: value.salt,
        storageNote: value.storageNote || '',
        allIngredientsAreBio: !!value.allIngredientsAreBio,
        printMhd: !!value.printMhd,
        printNutrition: value.printNutrition !== false,
        mhd: this.toDateOnly(value.mhd),
        charge: value.charge || '',
      };
    }

    return {
      ...value,
      ...this.getContentAndIngredients(),
      printMhd: value.printMhd !== false,
      mhd: this.toDateOnly(value.mhd),
      charge: value.charge || '',
    };
  }

  private shopwareMhd(): Date | null {
    const variantId = this.editObject?.articleVariantId ?? this.articleVariantIds?.[0];
    const variant = this.article?.articleVariants?.find(item => item.id === variantId)
      ?? this.article?.defaultArticleVariant;
    return this.parseMhd(variant?.mhd);
  }

  private parseMhd(value: string | Date | null | undefined): Date | null {
    if (!value) {
      return null;
    }
    const parsed = value instanceof Date ? value : new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  private toDateOnly(value: string | Date | null | undefined): string | null {
    const parsed = this.parseMhd(value);
    if (!parsed) {
      return null;
    }
    const year = parsed.getFullYear();
    const month = String(parsed.getMonth() + 1).padStart(2, '0');
    const day = String(parsed.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private prepareRichTextContent(val: string): string {
    if (!val) {
      return null;
    }

    const newVal = val
      .replace(/<\/p>/gi, '')
      .replace(/<p[^>]*>/gi, '<br>')
      .replace(/^(?:<br\s*\/?>|\s)+/gi, '')
      .replace(/(?:<br\s*\/?>|\s)+$/gi, '')
      .trim();

    return `<p>${UnicodeParser.replaceGlyphs(newVal)}</p>`;
  }

  private stripNutritionHeader(content: string): string {
    if (!content) {
      return content;
    }

    const headers = [this.nutritionHeaderPlain, this.nutritionHeaderGlyphs]
      .map(header => header.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
    const headerPattern = `(?:${headers.join('|')})(?:<br\\s*\\/?>)?`;

    return content
      .replace(new RegExp(headerPattern, 'gi'), '')
      .replace(/<p>(?:\s*<br\s*\/?>)*/i, '<p>');
  }

  private withNutritionHeader(content: string): string {
    if (!content) {
      return content;
    }

    const cleaned = this.stripNutritionHeader(content);
    return cleaned.replace('<p>', `<p>${this.nutritionHeaderGlyphs}<br>`);
  }

  private getContentAndIngredients(): { content: string, ingredients: string } {
    let content = this.prepareRichTextContent(this.form.value['content']);

    if (this.currentType === ArticleLabelTypes.Round50x50) {
      content = this.withNutritionHeader(content);
    }

    return {
      content,
      ingredients: this.prepareRichTextContent(this.form.value['ingredients']),
    };
  }
}
